/**
 * Environment Configuration Utility
 *
 * Priority chain (first non-empty wins):
 *   1. window.__ENV__  — runtime config injected by deploy script (env-config.js)
 *   2. Vite define     — build-time constants from .env.production via vite.config.ts
 *   3. process.env     — Node.js / Jest
 *   4. FAIL in production / localhost in development
 *
 * This ensures deployed builds ALWAYS use runtime config, even if the build
 * was accidentally done without .env.production.
 */

import {
  getEnvValue,
  getOptionalEnvValue,
  isProductionRuntime,
} from '@template-project/storefront-shared';

interface EnvConfig {
  PRODUCTS_API_URL: string;
  CARTS_API_URL: string;
  CHECKOUT_URL: string;
  CHECKOUT_MIDDLEWARE_URL: string;
  CAMPAIGNS_API_URL: string;
  CAMPAIGNS_TENANT_ID: string;
  FUNNELS_API_URL: string;
  FUNNELS_TENANT_ID: string;
  /** Store ID for multi-store filtering. Empty string means all stores (no filtering). */
  FUNNELS_STORE_ID: string;
  /** Google Tag Manager container ID (e.g. "GTM-XXXXXXX"). Empty string disables GTM. */
  GTM_ID: string;
  /** Public product feed URL (Google Merchant XML). Empty string disables the feeds page link. */
  PUBLIC_PRODUCT_FEED_URL: string;
  /** Public-facing base URL of the storefront (used for canonical links, OG tags). */
  BASE_URL: string;
  /** Storefront BFF base URL. Empty string means BFF is not available. */
  STOREFRONT_BFF_URL: string;
  /** Deploy/runtime fingerprint used to invalidate stale browser runtime state after deploys. */
  RUNTIME_VERSION: string;
}

// Declare global constants that Vite will replace at build time
declare const __CHECKOUT_URL__: string | undefined;
declare const __CHECKOUT_MIDDLEWARE_URL__: string | undefined;
declare const __PRODUCTS_API_URL__: string | undefined;
declare const __CARTS_API_URL__: string | undefined;
declare const __CAMPAIGNS_API_URL__: string | undefined;
declare const __CAMPAIGNS_TENANT_ID__: string | undefined;
declare const __FUNNELS_API_URL__: string | undefined;
declare const __FUNNELS_TENANT_ID__: string | undefined;
declare const __FUNNELS_STORE_ID__: string | undefined;
declare const __GTM_ID__: string | undefined;
declare const __PUBLIC_PRODUCT_FEED_URL__: string | undefined;
declare const __BASE_URL__: string | undefined;
declare const __STOREFRONT_BFF_URL__: string | undefined;
declare const __RUNTIME_VERSION__: string | undefined;
declare const __DEV__: boolean | undefined;

// Local dev defaults — only used by the Vite dev server.
const devDefaults: EnvConfig = {
  PRODUCTS_API_URL: 'http://products.dev.trafiflow.local',
  CARTS_API_URL: 'http://carts.dev.trafiflow.local',
  CHECKOUT_URL: 'http://localhost:8082',
  CHECKOUT_MIDDLEWARE_URL: 'http://localhost:8088',
  CAMPAIGNS_API_URL: 'http://localhost:8104',
  CAMPAIGNS_TENANT_ID: 'dev',
  FUNNELS_API_URL: 'http://funnels.dev.trafiflow.local',
  FUNNELS_TENANT_ID: 'dev',
  FUNNELS_STORE_ID: '',
  // GTM is disabled in local dev — set VITE_GTM_ID in .env.local to enable it locally
  GTM_ID: '',
  PUBLIC_PRODUCT_FEED_URL: '',
  BASE_URL: 'http://localhost:5174',
  STOREFRONT_BFF_URL: '',
  RUNTIME_VERSION: 'local-dev',
};

function dropLoopbackUrlInProduction(value: string): string {
  const production = isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);
  if (!production) {
    return value;
  }

  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(value) ? '' : value;
}

export const env: EnvConfig = {
  PRODUCTS_API_URL: getEnvValue({
    windowEnvKey: 'VITE_PRODUCTS_API_URL',
    viteGlobal: typeof __PRODUCTS_API_URL__ !== 'undefined' ? __PRODUCTS_API_URL__ : undefined,
    processEnvKey: 'VITE_PRODUCTS_API_URL',
    devDefault: devDefaults.PRODUCTS_API_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  CARTS_API_URL: getEnvValue({
    windowEnvKey: 'VITE_CARTS_API_URL',
    viteGlobal: typeof __CARTS_API_URL__ !== 'undefined' ? __CARTS_API_URL__ : undefined,
    processEnvKey: 'VITE_CARTS_API_URL',
    devDefault: devDefaults.CARTS_API_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  CHECKOUT_URL: getEnvValue({
    windowEnvKey: 'VITE_CHECKOUT_URL',
    viteGlobal: typeof __CHECKOUT_URL__ !== 'undefined' ? __CHECKOUT_URL__ : undefined,
    processEnvKey: 'VITE_CHECKOUT_URL',
    devDefault: devDefaults.CHECKOUT_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  CHECKOUT_MIDDLEWARE_URL: getEnvValue({
    windowEnvKey: 'VITE_CHECKOUT_MIDDLEWARE_URL',
    viteGlobal: typeof __CHECKOUT_MIDDLEWARE_URL__ !== 'undefined' ? __CHECKOUT_MIDDLEWARE_URL__ : undefined,
    processEnvKey: 'VITE_CHECKOUT_MIDDLEWARE_URL',
    devDefault: devDefaults.CHECKOUT_MIDDLEWARE_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  CAMPAIGNS_API_URL: getEnvValue({
    windowEnvKey: 'VITE_CAMPAIGNS_API_URL',
    viteGlobal: typeof __CAMPAIGNS_API_URL__ !== 'undefined' ? __CAMPAIGNS_API_URL__ : undefined,
    processEnvKey: 'VITE_CAMPAIGNS_API_URL',
    devDefault: devDefaults.CAMPAIGNS_API_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  CAMPAIGNS_TENANT_ID: getEnvValue({
    windowEnvKey: 'VITE_CAMPAIGNS_TENANT_ID',
    viteGlobal: typeof __CAMPAIGNS_TENANT_ID__ !== 'undefined' ? __CAMPAIGNS_TENANT_ID__ : undefined,
    processEnvKey: 'VITE_CAMPAIGNS_TENANT_ID',
    devDefault: devDefaults.CAMPAIGNS_TENANT_ID,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  FUNNELS_API_URL: getEnvValue({
    windowEnvKey: 'VITE_FUNNELS_API_URL',
    viteGlobal: typeof __FUNNELS_API_URL__ !== 'undefined' ? __FUNNELS_API_URL__ : undefined,
    processEnvKey: 'VITE_FUNNELS_API_URL',
    devDefault: devDefaults.FUNNELS_API_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  FUNNELS_TENANT_ID: getEnvValue({
    windowEnvKey: 'VITE_FUNNELS_TENANT_ID',
    viteGlobal: typeof __FUNNELS_TENANT_ID__ !== 'undefined' ? __FUNNELS_TENANT_ID__ : undefined,
    processEnvKey: 'VITE_FUNNELS_TENANT_ID',
    devDefault: devDefaults.FUNNELS_TENANT_ID,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  FUNNELS_STORE_ID: getOptionalEnvValue(
    'VITE_FUNNELS_STORE_ID',
    typeof __FUNNELS_STORE_ID__ !== 'undefined' ? __FUNNELS_STORE_ID__ : undefined,
    'VITE_FUNNELS_STORE_ID',
    devDefaults.FUNNELS_STORE_ID
  ),
  GTM_ID: getOptionalEnvValue(
    'VITE_GTM_ID',
    typeof __GTM_ID__ !== 'undefined' ? __GTM_ID__ : undefined,
    'VITE_GTM_ID',
    devDefaults.GTM_ID
  ),
  PUBLIC_PRODUCT_FEED_URL: getOptionalEnvValue(
    'VITE_PUBLIC_PRODUCT_FEED_URL',
    typeof __PUBLIC_PRODUCT_FEED_URL__ !== 'undefined' ? __PUBLIC_PRODUCT_FEED_URL__ : undefined,
    'VITE_PUBLIC_PRODUCT_FEED_URL',
    devDefaults.PUBLIC_PRODUCT_FEED_URL,
  ),
  BASE_URL: getEnvValue({
    windowEnvKey: 'VITE_BASE_URL',
    viteGlobal: typeof __BASE_URL__ !== 'undefined' ? __BASE_URL__ : undefined,
    processEnvKey: 'VITE_BASE_URL',
    devDefault: devDefaults.BASE_URL,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
  STOREFRONT_BFF_URL: dropLoopbackUrlInProduction(
    getOptionalEnvValue(
      'VITE_STOREFRONT_BFF_URL',
      typeof __STOREFRONT_BFF_URL__ !== 'undefined' ? __STOREFRONT_BFF_URL__ : undefined,
      'VITE_STOREFRONT_BFF_URL',
      devDefaults.STOREFRONT_BFF_URL,
    ),
  ),
  RUNTIME_VERSION: getEnvValue({
    windowEnvKey: 'VITE_RUNTIME_VERSION',
    viteGlobal: typeof __RUNTIME_VERSION__ !== 'undefined' ? __RUNTIME_VERSION__ : undefined,
    processEnvKey: 'VITE_RUNTIME_VERSION',
    devDefault: devDefaults.RUNTIME_VERSION,
    runtimeEnvMode: 'production-only',
    devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
  }),
};

export const isDev = !isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);

/**
 * Get the appropriate URL based on environment
 * In development with Vite dev server, returns proxy path
 * In production, returns the full API URL
 */
export function getApiUrl(apiName: keyof EnvConfig, proxyPath?: string): string {
  if (isDev && proxyPath) {
    return proxyPath;
  }
  return env[apiName];
}
